/**
 * @reflecto/client-core — sse/sse-client.ts
 *
 * Transport for the server's SSE stream. Framework-agnostic — uses only
 * `fetch`, `AbortController`, `ReadableStream`, and `TextDecoder`, all
 * of which are available in modern browsers and Service Workers. The
 * class itself does NOT schedule reconnects — after a disconnect it
 * simply returns; the host decides when to call `connect()` again.
 * The extension drives that with a `chrome.alarms` keepalive; other
 * hosts (web, Electron/Tauri) will each wire up their own reconnect
 * tick before relying on this transport for long-lived streams.
 */

export interface SSEEvent {
  event: string;
  data: string;
  id?: string;
}

export interface SSEClientOptions {
  /** Base API URL (e.g. `https://sync.reflecto.dev/v1`). The transport appends `/stream` to form the connection endpoint — callers must NOT include the path themselves. */
  url: string;
  /** JWT device token. Sent as `Authorization: Bearer <token>`. */
  token: string;
  /** Called for every parsed server event. Awaited so the loop serialises event processing. */
  onEvent: (event: SSEEvent) => void | Promise<void>;
  /** Called whenever the underlying connection transitions open/closed. */
  onStatusChange?: (connected: boolean) => void;
  /** High-water seqId cursor; sent as `Last-Event-ID` so the server skips already-seen queued messages. */
  lastEventId?: number;
  /**
   * Called ONLY when the server explicitly reports the pairing is gone — a body
   * of `{"error":"no_paired_device"}` (`routes/stream.ts:70`). Deliberately not
   * keyed to the status code: a 401, 403 or non-`no_paired_device` 404 is a
   * transport failure and stays retryable. Hosts treat this callback as an
   * unpair and destroy local state, so it must never fire on a guess — see
   * `isUnpairedCode`.
   */
  onPairingLost?: () => void;
  /**
   * Called for non-abort connection failures (network drop, non-2xx
   * status, parser exception). Diagnostic-only — the host still
   * receives `onStatusChange(false)` and is responsible for any
   * retry. AbortError is intentionally NOT surfaced here since it
   * represents an expected teardown initiated by the host itself.
   */
  onError?: (err: unknown) => void;
  /**
   * Called when the server REFUSES the handshake, with the status and the `error`
   * string from the body (or `null` when it sent none that could be read).
   *
   * Strictly informational, and deliberately separate from `onPairingLost`: this
   * fires for EVERY non-OK response, including the `no_paired_device` one, and
   * changes nothing about retryability. `onPairingLost` remains the only signal
   * that means "this pairing is gone", and it still fires only on that one code —
   * see `isUnpairedCode`. A host must never treat an `onRefused` status as an
   * unpairing; that inversion is exactly what the allowlist exists to prevent.
   *
   * Exists because a host may need to DISTINGUISH refusals to advise the user
   * correctly. `apps/cli`'s `tail` gives three different recoveries for
   * `no_paired_device`, `token_expired`, and everything else, and only the first
   * recommends `reflecto unpair`, which deletes the server-side device row. A bare
   * boolean cannot carry that distinction, so without this the CLI would have to
   * keep its own forked transport to keep it.
   */
  onRefused?: (status: number, errorCode: string | null) => void;
}

/**
 * The one error code that means this pairing no longer exists.
 *
 * `apps/server/src/routes/stream.ts:70` is the ONLY place `/v1/stream` reports a
 * lost pairing, and it says so in the body. Its other refusals are 400s, and the
 * 401/403 a caller may see come from global middleware — `token_expired` and
 * `origin_not_allowed` — neither of which is a statement about pairing.
 */
const UNPAIRED_ERROR_CODE = "no_paired_device";

/**
 * The server's `error` string from a refused response, or `null` when it did not send
 * one that could be read.
 *
 * PARSING ONLY — this makes no judgement about what the code MEANS. The allowlist
 * decision lives in `isUnpairedCode` below, and so does the reasoning behind it.
 *
 * Every failure to extract a string resolves to `null`: an empty body, a non-JSON
 * body, a JSON body that is not an object, an `error` field that is not a string, or
 * a body that cannot be read at all. Callers must treat `null` as an absence of
 * information rather than a fact.
 */
async function readRefusalCode(res: Response): Promise<string | null> {
  try {
    const text = await res.text();
    if (!text) return null;
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed !== "object" || parsed === null) return null;
    const code = (parsed as { error?: unknown }).error;
    return typeof code === "string" ? code : null;
  } catch {
    // Unreadable or unparseable. An absence of information, not a fact.
    return null;
  }
}

/**
 * Is this refusal the server telling us the pairing is gone?
 *
 * ALLOWLIST, NOT DENYLIST — and that inversion is the whole point. This branch
 * used to fire on `404 || 401 || 403`, and what the hosts do with the signal is a
 * full local wipe: notification history, conversation threads, clipboard history,
 * in-flight transfers, and `phonePubKey` — half the E2E key exchange, with no
 * account to restore it from. Recovery is re-pairing from the phone, which mints
 * a new device identity and orphans the old row.
 *
 * Matching on status meant three unrelated causes could destroy user data:
 *   - 401 `invalid_token` is what every device receives the instant `JWT_SECRET`
 *     is rotated — one server-side action, every install wipes at once.
 *   - 403 `token_expired` is normally staggered across devices (each token's
 *     expiry is stamped at its own pairing time), but `DEVICE_TOKEN_IDLE_DAYS=0`
 *     — documented as an instant revert — makes every already-expired token fail
 *     simultaneously, turning it into the same fleet-wide event.
 *   - A proxy 404 or a wrong API base URL looks exactly like a real unpairing.
 *
 * So: unless the server names it, treat the refusal as transport and stay
 * retryable. Every ambiguity — empty body, non-JSON body, unknown code, a body
 * that cannot be read at all — resolves to `false`. Losing a session that really
 * was unpaired costs one stale reconnect loop the user can end from Settings;
 * guessing the other way costs data that cannot be recovered.
 *
 * (This mirrors what Android already does — `StalePairingDetection.kt` inspects
 * the body and takes no action on an unrecognised 403.)
 *
 * Split out from the body read (`readRefusalCode`) so the code can ALSO be handed to
 * `onRefused` without reading the body twice — a Response body is a stream and can
 * only be consumed once, so a second `res.text()` would throw. The allowlist itself
 * is unchanged by that split: only this exact string, and every ambiguity still
 * resolves to `false`.
 */
function isUnpairedCode(code: string | null): boolean {
  return code === UNPAIRED_ERROR_CODE;
}

export class SSEClient {
  private controller: AbortController | null = null;
  private _connected = false;
  private options: SSEClientOptions | null = null;

  get isConnected(): boolean {
    return this._connected;
  }

  connect(options: SSEClientOptions): void {
    this.options = options;
    this.startConnection();
  }

  disconnect(): void {
    // cleanup() calls setConnected(false) which fires onStatusChange — must happen
    // before options is nulled, otherwise the callback is silently dropped.
    this.cleanup();
    this.options = null;
  }

  /**
   * Wall-clock time of the last byte read off the stream, 0 before the first
   * connection. Deliberately taken at the BYTE level, not in `onEvent`: the
   * server's liveness signal is a `: ping` comment every 30s, and a comment
   * parses to zero events, so an event-level timestamp would report a healthy
   * quiet stream as dead.
   */
  private lastDataAt = 0;

  /**
   * Invalidates in-flight connection attempts. Bumped by `cleanup()`, i.e. by
   * `disconnect()` and by the start of each new attempt, so a superseded
   * attempt can never write `_connected` (or fire `onError`) after a newer
   * one has taken over — which would otherwise flip a live connection's
   * status to `false` and send the host into a reconnect loop.
   */
  private epoch = 0;

  /**
   * True when the connection is up but nothing has arrived for `maxIdleMs`.
   *
   * `isConnected` alone cannot answer "is this stream working?" — it reports
   * that the connection was ESTABLISHED, and goes false only when the read
   * loop EXITS. A loop stalled on a half-open socket (sleep/wake, network
   * change — no FIN is ever delivered, and `fetch` has no read timeout) or on
   * an `onEvent` handler that never resolves has not exited, so a host that
   * gates its reconnect on `isConnected` alone will never reconnect. Silence
   * has to be a signal in its own right.
   *
   * Never stale while disconnected: that state is the host's `isConnected`
   * check, and reporting it here too would double-trigger the same reconnect.
   */
  isStale(maxIdleMs: number): boolean {
    if (!this._connected || this.lastDataAt === 0) return false;
    return Date.now() - this.lastDataAt > maxIdleMs;
  }

  private cleanup(): void {
    this.epoch++;
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
    this.setConnected(false);
  }

  private setConnected(value: boolean): void {
    if (this._connected !== value) {
      this._connected = value;
      this.options?.onStatusChange?.(value);
    }
  }

  private async startConnection(): Promise<void> {
    if (!this.options) return;

    this.cleanup();
    // AFTER cleanup(), which bumps the epoch: this attempt owns the value it
    // just produced, and any LATER cleanup() (a reconnect, or disconnect())
    // invalidates this one.
    const myEpoch = this.epoch;
    const { url, token, onEvent, lastEventId } = this.options;
    this.controller = new AbortController();

    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (typeof lastEventId === "number" && lastEventId > 0) {
      headers["Last-Event-ID"] = String(lastEventId);
    }

    try {
      const res = await fetch(`${url}/stream`, {
        headers,
        signal: this.controller.signal,
      });

      if (this.epoch !== myEpoch) {
        // Superseded while the fetch was in flight (the abort landed a tick
        // too late to reject it). Checked BEFORE any branching on the status:
        // `options` is shared across attempts, so a stale 401/403/404 reaching
        // the pairing-lost branch below would fire `onPairingLost` for — and
        // null `options` out from under — the newer, still-valid attempt,
        // silently muting its `onStatusChange`/`onError` for the rest of its
        // life. Release the body too; nothing below will read it, and an
        // un-cancelled stream holds the connection open.
        void res.body?.cancel().catch(() => {});
        return;
      }

      if (!res.ok || !res.body) {
        // ONE body read, two consumers. `onRefused` gets the raw status and code;
        // the pairing-lost branch below gets the allowlist verdict derived from the
        // same string. Reading twice is impossible — the body is a stream.
        const code = await readRefusalCode(res);
        this.options?.onRefused?.(res.status, code);
        if (isUnpairedCode(code)) {
          // Pairing is genuinely gone — don't reconnect, notify caller
          this.options?.onPairingLost?.();
          this.options = null; // prevent reconnect
          return;
        }
        throw new Error(`SSE connect failed (${res.status})`);
      }

      this.lastDataAt = Date.now();
      this.setConnected(true);

      let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
      try {
        reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          // NO final `decoder.decode(new Uint8Array(), { stream: false })` here, and
          // that is deliberate — `apps/cli`'s soon-to-be-deleted forked transport had
          // one, so this records why it is not ported rather than leaving a future
          // reader to "restore" it.
          //
          // Its comment claimed a multi-byte character split across the final chunk
          // boundary was "silently dropped". Measured, it is not: every byte the
          // server sent is delivered by a `read()` before `done`, so the streaming
          // decoder holds nothing at the end and the flush returns "". The only case
          // where it returns anything is a stream truncated MID-character, where it
          // yields U+FFFD — a replacement character, not the lost data — which cannot
          // complete an SSE `\n\n` boundary, so the partial event is not dispatched
          // either way. The flush recovers nothing and changes no observable output.
          if (done) break;
          // Every chunk counts as liveness, keepalive comments included —
          // see `lastDataAt`.
          this.lastDataAt = Date.now();

          buffer += decoder.decode(value, { stream: true });
          const events = parseSSEBuffer(buffer);
          buffer = events.remaining;

          for (const event of events.parsed) {
            await onEvent(event);
          }
        }
      } finally {
        // Release the body lock on every exit path (normal loop exit,
        // thrown error, or AbortError). Without this the reader stays
        // locked and the HTTP connection can be held open until the
        // host tears the page / worker down — including across
        // extension service-worker suspension/revival cycles and
        // web-app tab hibernation.
        reader?.cancel().catch(() => {});
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        // Intentional disconnect — `disconnect()` already called
        // `cleanup()` which fired `setConnected(false)`. Returning
        // here avoids a redundant second `setConnected(false)` call
        // and the duplicate `onStatusChange(false)` it would emit.
        return;
      }
      // Non-abort error: connection failed unexpectedly. Surface the
      // reason via onError for diagnostics, then fall through to
      // notify the host of the disconnected state below.
      if (this.epoch !== myEpoch) return; // a newer attempt owns the state now
      this.options?.onError?.(err);
    }

    // Reached on natural stream end (server closed) or a swallowed
    // non-abort error. SSEClient does not schedule reconnects — the
    // host (e.g. the extension's chrome.alarms keepalive) is
    // responsible for calling connect() again if it wants to retry.
    if (this.epoch !== myEpoch) return; // superseded — must not mark the LIVE connection dead
    this.setConnected(false);
  }
}

/**
 * Parse a raw SSE buffer into discrete events. Handles `\n\n` boundaries,
 * comments, multi-line data, and CRLF normalisation.
 *
 * CRLF handling: `\r\n` pairs are collapsed to `\n` before splitting.
 * A lone trailing `\r` at the end of the buffer is kept as-is so a
 * CRLF split across two reader chunks (buffer ends with `\r`, next
 * chunk starts with `\n`) reassembles cleanly into `\r\n` → `\n` on
 * the subsequent call. The per-line loop strips any residual trailing
 * `\r` defensively.
 */
export function parseSSEBuffer(buffer: string): { parsed: SSEEvent[]; remaining: string } {
  const normalised = buffer.replace(/\r\n/g, "\n");
  const parsed: SSEEvent[] = [];
  const blocks = normalised.split("\n\n");

  // Last element is incomplete if buffer didn't end with \n\n
  const remaining = normalised.endsWith("\n\n") ? "" : (blocks.pop() ?? "");

  for (const block of blocks) {
    if (!block.trim()) continue;

    let event = "message";
    let data = "";
    let id: string | undefined;
    let sawField = false;

    for (const rawLine of block.split("\n")) {
      const line = rawLine.replace(/\r$/, "");
      if (line.startsWith(":")) continue; // SSE comment (e.g. ": ping", ":keepalive", ":")
      if (line === "") continue; // blank line inside a block has no effect

      // Per the SSE spec: a line with no colon is treated as a field
      // name with an empty value (e.g. a bare `data` line appends the
      // empty string to the data buffer).
      const colonIdx = line.indexOf(":");
      const field = colonIdx === -1 ? line : line.slice(0, colonIdx);
      const value = colonIdx === -1 ? "" : line.slice(colonIdx + 1).trimStart();

      switch (field) {
        case "event":
          event = value;
          sawField = true;
          break;
        case "data":
          data += (data ? "\n" : "") + value;
          sawField = true;
          break;
        case "id":
          id = value;
          sawField = true;
          break;
      }
    }

    // The SSE spec dispatches any block that supplied at least one
    // recognised field — including an explicit `event:` with no
    // `data:` body. Dropping those would silently swallow server
    // signals (e.g. a bare "confirmed" or "expired" event).
    if (sawField) {
      parsed.push({ event, data, id });
    }
  }

  return { parsed, remaining };
}
